import { useMemo, useState, Fragment } from 'react';
import { Box, SxProps, Typography, useTheme } from '@mui/material';
import { Button } from 'system/atoms';
import RemoveRedEyeIcon from '@mui/icons-material/RemoveRedEye';
import { CsvPreviewTable } from '../../pages/Campaigns/subpages/components/CsvPreview';
import { FormikProps } from 'formik';
import { FormValues } from 'pages/CampaignCreation/types';
import { FieldWrapper, Select } from 'common/form';

const columnItemContainer: SxProps = {
  display: 'flex',
  width: '100%'
};

const PreviewItem = ({ onClick }: { onClick: () => void }) => {
  const {
    palette: { primary }
  } = useTheme();

  const color = primary.main;

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', ml: 1 }}>
      <Button variant="tertiary" onClick={onClick}>
        <RemoveRedEyeIcon sx={{ color }} />
        <Typography sx={{ color, ml: 1, fontWeight: 'bold' }}>
          Preview
        </Typography>
      </Button>
    </Box>
  );
};

const ColumnItem = ({
  label,
  columns,
  value,
  onChange,
  onPreviewClick,
  error,
  touched,
  handleBlur,
  name
}: {
  label: string;
  columns: string[];
  value: string;
  onChange: (value: string) => void;
  onPreviewClick: () => void;
  onCheckboxToggle?: (value: boolean) => void;
  disableSelection: boolean;
  error: string | undefined;
  touched: boolean | undefined;
  handleBlur: (
    e: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => void;
  name: string;
}) => {
  const options = useMemo(() => {
    const selectOptions = columns.map((value) => ({ value, label: value }));

    return [
      {
        value: '',
        label: 'Choose column'
      },
      ...selectOptions
    ];
  }, [columns]);

  return (
    <Box sx={columnItemContainer}>
      <FieldWrapper errorMessage={touched ? error : undefined} label={label}>
        <Box display="flex" alignItems="center">
          <Select
            name={name}
            value={value || ''}
            onChange={onChange}
            onBlur={handleBlur}
            field={{
              options,
              displayEmpty: true
            }}
          />
          <Box mb={-1} width={160}>
            {value && <PreviewItem onClick={onPreviewClick} />}
          </Box>
        </Box>
      </FieldWrapper>
    </Box>
  );
};

type ColumnConfig = {
  label: string;
  name: 'firstName' | 'lastName' | 'email' | 'phoneNumber';
  previewTitle: string;
  disabled?: boolean;
  order?: number;
};

type ColumnMapperProps = {
  columnsConfig: ColumnConfig[];
  columns: string[];
  numRows: number;
  csvData: any[];
  successText?: string;
  formik: FormikProps<FormValues>;
};

export const ColumnsMapper: React.FC<ColumnMapperProps> = ({
  columnsConfig,
  columns,
  numRows,
  csvData,
  formik
}) => {
  const [isModalPreviewOpen, setIsModalPreviewOpen] = useState(false);
  const [columnSelected, setColumnSelected] = useState('');

  const initialEnabledColumnState = useMemo(() => {
    return columnsConfig.reduce((acc, c) => {
      return {
        ...acc,
        [c.name]: {
          isEnabled: false
        }
      };
    }, {});
  }, [columnsConfig.length]);

  const [enabledColumnsState, setEnabledColumnState] = useState<{
    [c: string]: { isEnabled: boolean };
  }>(initialEnabledColumnState);

  const getDataForColumn = () => {
    const columnIndex = columns.findIndex((item) => item === columnSelected);
    const data = csvData.reduce((acc, row) => {
      const value = row[columnIndex];

      return [...acc, [value]];
    }, []);

    return {
      data,
      columnsSelected: [columns[columnIndex]]
    };
  };

  const { data, columnsSelected } = getDataForColumn();

  const { previewTitle, previewSubtitle } = useMemo(() => {
    const column = columnsConfig?.find((c) => c.name === columnSelected);

    return {
      previewTitle: column?.previewTitle || '',
      previewSubtitle: `${csvData.length} rows taken from Column: ${columnsSelected[0]}`
    };
  }, [columnsSelected, csvData.length, columnSelected]);

  if (!numRows) return null;

  const updateColumState = (columnMap: string, isEnabled: boolean) => {
    const newEnabledColumnState = {
      ...enabledColumnsState,
      [columnMap]: {
        isEnabled
      }
    };

    setEnabledColumnState(newEnabledColumnState);
  };

  return (
    <>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'flex-start'
        }}
      >
        {columnsConfig
          .sort((a, b) => (a.order || 0) - (b.order || 0))
          .map((config, index) => (
            <Fragment key={config.name}>
              <ColumnItem
                disableSelection={config.disabled || false}
                label={config.label}
                columns={columns}
                value={formik.values.csvColumns?.[config.name] || ''}
                onChange={(value) =>
                  formik.setFieldValue(`csvColumns.${config.name}`, value)
                }
                onPreviewClick={() => {
                  setIsModalPreviewOpen(true);

                  setColumnSelected(
                    formik.values.csvColumns?.[config.name] || ''
                  );
                }}
                onCheckboxToggle={(value) => {
                  updateColumState(config.name, value);
                  if (!value) {
                    formik.setFieldValue(`csvColumns.${config.name}`, '');
                  }
                }}
                error={formik.errors.csvColumns?.[config.name]}
                touched={formik.touched.csvColumns?.[config.name]}
                name={`csvColumns.${config.name}`}
                handleBlur={formik.handleBlur}
              />
              {index < columnsConfig.length - 1 && <Box height={16} />}
            </Fragment>
          ))}
      </Box>
      {data.length > 0 && columnsSelected.length > 0 && (
        <CsvPreviewTable
          columns={columnsSelected}
          csvData={data}
          isOpen={isModalPreviewOpen}
          handleClose={() => {
            setIsModalPreviewOpen(false);
          }}
          title={previewTitle}
          subTitle={previewSubtitle}
        />
      )}
    </>
  );
};
